/**
 * The projections a course write invalidates (issue #473).
 *
 * A section's identity, placement, publication state, and roster are read by
 * six different surfaces. #473 established that every course mutation refreshes
 * all of them rather than the two the writing page happened to be looking at,
 * which is what left a stale count behind on the pages nobody thought about.
 *
 * The list lives here rather than inside a page because issue #485 gave course
 * creation and publication a SECOND owner — Course Setup — and two pages each
 * holding their own copy is exactly how the two would drift.
 */
import type { QueryClient } from "@tanstack/react-query";
import { PEOPLE_QUERY_KEY } from "@/lib/api/people";

export const COURSE_MUTATION_QUERY_KEY_PREFIXES = [
  ["courses"],
  ["course"],
  ["course-history"],
  ["dashboard"],
  ["seat-workspace"],
  ["teaching-courses"],
  ["learning-courses"],
  ["course-home"],
  ["course-record-review"],
] as const;

export function invalidateCourseDependentQueries(queryClient: QueryClient, courseId?: string) {
  if (courseId) {
    void queryClient.invalidateQueries({ queryKey: ["course-roster", courseId] });
    void queryClient.invalidateQueries({ queryKey: ["enrollment-notice"] });
  }
  for (const queryKey of COURSE_MUTATION_QUERY_KEY_PREFIXES) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

/** Roster writes also change person readiness; conflicts can reveal a closed term. */
export function invalidateRosterDependentQueries(queryClient: QueryClient, courseId: string) {
  invalidateCourseDependentQueries(queryClient);
  for (const queryKey of [
    ["course-roster", courseId],
    ["course-roster-candidates", courseId],
    ["post-admission-readiness"],
    PEOPLE_QUERY_KEY,
    ["semesters"],
  ]) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

export async function refreshCourseAfterAct(client: QueryClient, courseId: string) {
  const reads = { queryKey: ["course-home"], predicate: (query: { queryKey: readonly unknown[] }) => query.queryKey[4] === courseId };
  // Invalidation alone joins an unfinished initial fetch. That response may
  // describe the course before this act, even when it arrives afterwards.
  await client.cancelQueries(reads);
  await client.invalidateQueries(reads);
}
